import { useContext, useEffect, useState } from 'react';
import { Grid, Flexbox, Divider, Button, Accordion, Sizes } from '@sede-enterprise/shell-ds-react-framework';
import 'leaflet/dist/leaflet.css';
import LineChart from './SnapshotLineChart/SnapshotLineChart';
import { SnapshotReportFilterSection } from './SnapshotReportFilterSection/SnapshotReportFilterSection';
import { AppContext } from '../../../../Context/AppContext';
import SnapshotWaterfallChart from './SnapshotWaterfallChart/SnapshotWaterfallChart';
import NcfReportingHomeHeader from '../NcfReportingUtils/NcfReportingHomeHeader/NcfReportingHomeHeader';
import { getAnalyticsSnapshotReportsData } from '../../../../api/services';
import { SelectFormField } from '../../../ReUsableComponents/SelectFormField/SelectFormField';
import { ASIA_MALAYSIA, ASIA_INDIA, ASIA_JAPAN, ASIA_PHILIPPINES, REGION_NAMES } from '../../../../api/constants';
import { SnapshotContainer, SnapshotLabel } from './SnapshotReport.style';
import { errorNotifyMessage, filterRegionsByAsiaRegion } from '../../../Utils/utils';
import NcfReportingLoader from '../NcfReportingLoader/NcfReportingLoader';
import { notify } from '../../../../../CarbonDashboard/components/Toast/notify';
export const SnapshotReport = () => {
  const [reportSelected, setReportSelected] = useState<string[]>([]);
  const [isReportDataLoading, setIsReportDataLoading] = useState(false);
  const { state: appDataState, dispatch: appDataDispatch } = useContext(AppContext).appData;
  const { state: snapshotState, dispatch: snapshotDispatch } =
    useContext(AppContext).analyticsSnapshotReport;
  const userRegion = localStorage.getItem('userRegion') ?? appDataState.userRegion;
  const snapshotReportOptions = snapshotState.analyticsSnapshotReportFilterOptions.snapshotReports;
  const isAsiaUser =
    localStorage.getItem('isAsiaUser') !== null
      ? localStorage.getItem('isAsiaUser') === 'true'
      : appDataState.userRegionFlags.isAsiaUser;
  const regionList = snapshotState.analyticsSnapshotReportFilterOptions.region;
  const asiaRegionList: string[] = filterRegionsByAsiaRegion(regionList, isAsiaUser);
  useEffect(() => {
    appDataDispatch({ type: 'setCurrentPage', payload: '' });
  }, [appDataDispatch]);

  useEffect(() => {
    const currentRegion = snapshotState.analyticsSnapshotReportFilteredValues.region;
    const defaultRegion = isAsiaUser ? asiaRegionList : [userRegion];

    // Only dispatch if region is not set or differs
    const shouldUpdateRegion =
      !currentRegion?.length ||
      JSON.stringify(currentRegion) !== JSON.stringify(defaultRegion);

    if (shouldUpdateRegion) {
      snapshotDispatch({
        type: 'setAnalyticsSnapshotReportFilteredValues',
        payload: {
          ...snapshotState.analyticsSnapshotReportFilteredValues,
          region: defaultRegion,
        },
      });
    }
  }, [snapshotState.analyticsSnapshotReportFilterOptions.region]);

  useEffect(() => {
    const {
      year,
      quarter,
      month,
      countryCode,
      commodity,
      reportStatus,
      transactionType,
      region,
    } = snapshotState.analyticsSnapshotReportFilteredValues;

    const fetchSnapshotReportsData = async () => {
      setIsReportDataLoading(true);
      try {
        const payload = {
          year,
          quarter,
          month,
          region,
          countryCode,
          commodity,
          reportStatus,
          transactionType,
        };
        const response = await getAnalyticsSnapshotReportsData(payload);
        snapshotDispatch({
          type: 'setAnalyticsSnapshotReportsData',
          payload: response.data,
        });
        setIsReportDataLoading(false);
      } catch (error) {
         notify('error', errorNotifyMessage(error));
      }
      setIsReportDataLoading(false);
    };

    fetchSnapshotReportsData();
    const { snapshotReports } = snapshotState.analyticsSnapshotReportFilteredValues;
    setReportSelected(snapshotReports ?? []);
  }, [snapshotState.analyticsSnapshotReportFilteredValues]);


  useEffect(() => {
    const { snapshotReports } = snapshotState.analyticsSnapshotReportFilteredValues;
    setReportSelected(snapshotReports ?? []);
  }, [snapshotState.analyticsSnapshotReportFilteredValues]);

  const handleApplyFilters = () => {
    const payload = {
      ...snapshotState.analyticsSnapshotReportFilteredValues,
      snapshotReports: reportSelected,
    };

    snapshotDispatch({
      type: 'setAnalyticsSnapshotReportFilteredValues',
      payload,
    });

    // Mark that generate data button has been clicked
    if (reportSelected.length > 0) {
      setIsDataGenerated(true);
    }
  };

  const filteredRegions = snapshotState.analyticsSnapshotReportFilteredValues?.region?.length
    ? snapshotState.analyticsSnapshotReportFilteredValues.region
    : Object.values(REGION_NAMES);

  // Create accordion items - only needed if userRegion is empty (global view)
  const regionAccordionItems = [];
  const regionAmericaKey = 'region-americas';

  // Only build accordion items if userRegion is empty
  if (userRegion === '') {
    // Check if we have AMERICAS region
    const americasRegions = filteredRegions?.filter((region) => region === 'AMERICAS');

    // Add AMERICAS region as the first accordion item
    if (americasRegions?.length > 0) {
      regionAccordionItems.push({
        label: 'Americas Volumes',
        key: regionAmericaKey,
        children: (
          <Flexbox gap='10px' flexWrap='wrap'>
            {americasRegions.map((region) => (
              <SnapshotWaterfallChart region={region} key={region} />
            ))}
          </Flexbox>
        ),
      });
    }

    // Check if we have ASIA regions
    const asiaRegions = filteredRegions?.filter((region: string) =>
      region.startsWith('ASIA-')
    );
    // Add ASIA regions as a single accordion item
    if (asiaRegions?.length > 0) {
      regionAccordionItems.push({
        label: 'Asia Volumes',
        key: 'region-asia',
        children: (
          <Flexbox gap='10px' flexWrap='wrap'>
            {asiaRegions.map((region) => (
              <SnapshotWaterfallChart region={region} key={region} />
            ))}
          </Flexbox>
        ),
      });
    }

    // Skip AMERICAS and ASIA regions as they are already handled
    const excludedRegions = ['AMERICAS', ASIA_JAPAN, ASIA_PHILIPPINES, ASIA_INDIA, ASIA_MALAYSIA];

    // Add other regions individually
    const sortedOtherRegions = filteredRegions
      ?.filter((region) => !excludedRegions.includes(region))
      .sort((a, b) => a.localeCompare(b));

    sortedOtherRegions?.forEach((region) => {
      regionAccordionItems.push({
        label: `${region.charAt(0).toUpperCase() + region.slice(1).toLowerCase()} Volumes`,
        key: `region-${region}`,
        children: <SnapshotWaterfallChart region={region} />,
      });
    });
  }

  // Default active key - set to AMERICAS if present, otherwise the first region
  let defaultAccordionKey: string[] = [];
  if (regionAccordionItems.length > 0) {
    if (regionAccordionItems[0].key === regionAmericaKey) {
      defaultAccordionKey = [regionAmericaKey];
    } else {
      defaultAccordionKey = [regionAccordionItems[0].key];
    }
  }

  const [isDataGenerated, setIsDataGenerated] = useState(false); // New state
  useEffect(() => {
    if (reportSelected.length === 0) {
      setIsDataGenerated(false);
    }
  }, [reportSelected]);
  const snapshotCondition = isDataGenerated && reportSelected.length > 0;
  return (
    <div style={{ height: '100%', background: '#F5F5F5' }} data-testid='snapshot-report'>
      <Flexbox gap='24px 24px' style={{ marginLeft: '15px', justifyContent: 'start' }}>
        <SnapshotReportFilterSection />
        { isReportDataLoading ? <NcfReportingLoader open={true} message='Getting Report Names !!!'/> :
        <Grid gap='10px'>
          <Grid.Cell>
            <Flexbox justifyContent='space-between'>
              <NcfReportingHomeHeader
                title='Data Snapshot Report'
                description='Data Snapshot Report'
              />
            </Flexbox>
            <Divider size='medium' direction='horizontal' style={{ marginTop: '20px' }} />
            <div data-testid='filter' style={{ width: '100%', marginTop: '20px' }}>
              <Flexbox flexDirection='row' alignItems='center' gap='20px' style={{ width: '60%' }}>
                <SelectFormField
                  id='report'
                  label='Select the report to view data'
                  testId='Report'
                  value={reportSelected}
                  onChange={setReportSelected}
                  options={snapshotReportOptions}
                  mode='multiple'
                />
                <Button
                  className='getDataBtn'
                  data-testid='Generate Data'
                  onClick={handleApplyFilters}
                  style={{
                    marginTop: '9px',
                    width: '25%',
                  }}
                  size='medium'
                >
                  Generate Data
                </Button>
              </Flexbox>
              {!isDataGenerated && (
                <SnapshotContainer>
                  <SnapshotLabel>
                    Please select a data snapshot to visualise the report
                  </SnapshotLabel>
                </SnapshotContainer>
              )}
            </div>
          </Grid.Cell>
          <Grid.Cell>{snapshotCondition && <LineChart />}</Grid.Cell>
          {snapshotCondition &&
            <Grid.Cell>
              {userRegion === '' ? (
                // User region is empty (global view) - use Accordion

                <Accordion
                  items={regionAccordionItems}
                  defaultActiveKey={defaultAccordionKey}
                  size={Sizes.Medium}
                />
              ) : (

                <Flexbox gap='10px' flexWrap='wrap'>
                  {filteredRegions.map((region) => (
                    <SnapshotWaterfallChart region={region} key={region} />
                  ))}
                </Flexbox>
              )}
            </Grid.Cell>
          }
        </Grid>}
      </Flexbox>
    </div>
  );
};
